import { Link } from 'react-router-dom'
import { Collapsible, CollapsibleItem, Dropdown, Icon } from 'react-materialize'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useOktaAuth } from '@okta/okta-react'
import {
  NavContainer,
  NavLink,
  Username,
  OtherItems,
  Options,
  StyledDropdown,
  StyledCollapsible,
  InactiveLink,
} from './Elements'
import AddTransactionsButton from '../AddTransactionsButton'
import CasNav from './CasNav'
import UmcNav from './UmcNav'
import { AuthPermissions } from '../../../Utilities/enum'

interface Props {
  username?: string
  permissions?: string[]
  isMobile: boolean
  onNavLinkClick: () => void
}

const Nav = ({
  username = 'User',
  permissions = [],
  isMobile,
  onNavLinkClick = () => {},
}: Props) => {
  const { casSection, cmsSection, umcCustomReports } = useFlags()
  const { oktaAuth, authState } = useOktaAuth()

  const login = () => oktaAuth.signInWithRedirect()
  const logout = () => oktaAuth.signOut()

  const shouldShowCustomReports = permissions.includes('tenant:view')
  const shouldShowUserManagementSelection = permissions.includes(
    AuthPermissions.userRead
  )
  const shouldShowTransactions = permissions.includes(
    AuthPermissions.reconciliationRead
  )
  const shouldShowCasReports = permissions.includes(AuthPermissions.reportsRead)

  const shouldShowCasSection = shouldShowCasReports || shouldShowTransactions
  const shouldShowUmcSection = permissions.includes(
    AuthPermissions.customerRead
  )
  const hasSmeReportsPermission = permissions.includes(
    AuthPermissions.customerPiiRead
  )

  const renderNav = () => {
    if (
      casSection &&
      cmsSection &&
      shouldShowUmcSection &&
      shouldShowCasSection
    ) {
      if (isMobile)
        return (
          <StyledCollapsible>
            <Collapsible accordion={true}>
              <CollapsibleItem expanded={false} header="CAS" node="div">
                <CasNav
                  shouldShowTransactions={shouldShowTransactions}
                  shouldShowReports={shouldShowCasReports}
                  onNavLinkClick={onNavLinkClick}
                  LinkComponent={Link}
                />
              </CollapsibleItem>
              <CollapsibleItem expanded={false} header="UMC" node="div">
                <UmcNav
                  onNavLinkClick={onNavLinkClick}
                  LinkComponent={Link}
                  InactiveLinkComponent={InactiveLink}
                  shouldShowReports={
                    umcCustomReports && hasSmeReportsPermission
                  }
                />
              </CollapsibleItem>
            </Collapsible>
          </StyledCollapsible>
        )
      return (
        <>
          <StyledDropdown>
            <Dropdown
              id="CASdropdown"
              options={{
                alignment: 'left',
                constrainWidth: false,
                coverTrigger: false,
                outDuration: 150,
              }}
              trigger={
                <Username>
                  CAS<Icon>arrow_drop_down</Icon>
                </Username>
              }
            >
              <CasNav
                shouldShowReports={shouldShowCasReports}
                shouldShowTransactions={shouldShowTransactions}
                onNavLinkClick={onNavLinkClick}
                LinkComponent={Link}
              />
            </Dropdown>
          </StyledDropdown>
          <StyledDropdown>
            <Dropdown
              id="UMCDropdown"
              options={{
                alignment: 'left',
                constrainWidth: false,
                coverTrigger: false,
                outDuration: 150,
              }}
              trigger={
                <Options>
                  UMC<Icon>arrow_drop_down</Icon>
                </Options>
              }
            >
              <UmcNav
                onNavLinkClick={onNavLinkClick}
                LinkComponent={Link}
                InactiveLinkComponent={InactiveLink}
                shouldShowReports={umcCustomReports && hasSmeReportsPermission}
              />
            </Dropdown>
          </StyledDropdown>
        </>
      )
    }
    if (casSection && shouldShowCasSection)
      return (
        <CasNav
          shouldShowTransactions={shouldShowTransactions}
          shouldShowReports={shouldShowCasReports}
          onNavLinkClick={onNavLinkClick}
          LinkComponent={NavLink}
        />
      )
    if (cmsSection && shouldShowUmcSection)
      return (
        <UmcNav
          onNavLinkClick={onNavLinkClick}
          LinkComponent={NavLink}
          InactiveLinkComponent={NavLink}
          shouldShowReports={umcCustomReports && hasSmeReportsPermission}
        />
      )
  }
  return (
    <NavContainer>
      {renderNav()}
      <OtherItems>
        {casSection && <AddTransactionsButton />}
        <Dropdown
          id="NavDropdown"
          options={{
            alignment: 'right',
            constrainWidth: false,
            coverTrigger: true,
            outDuration: 150,
          }}
          trigger={
            <Username>
              {username}
              <Icon>arrow_drop_down</Icon>
            </Username>
          }
        >
          {authState.isAuthenticated ? (
            <Link to="#" onClick={logout}>
              Sign out
            </Link>
          ) : (
            <Link onClick={login} to="/">
              Sign in
            </Link>
          )}
          {cmsSection && shouldShowUserManagementSelection && (
            <Link to="/user-management">User Management</Link>
          )}
          {casSection && shouldShowCustomReports && (
            <Link to="/reports/custom">Custom Reports</Link>
          )}
          {cmsSection && (
            <a href="mailto:enterprisesupport@taxbit.com">Contact Us</a>
          )}
        </Dropdown>
      </OtherItems>
    </NavContainer>
  )
}

export default Nav
